/**
 * Safe Lazy Loading Utility
 * 
 * Provides error handling for React.lazy() chunk loading failures.
 * Wraps lazy imports with error handling to catch and report chunk loading errors.
 * 
 * @author AI Assistant
 * @version 1.0.0
 */

import React, { lazy, ComponentType, LazyExoticComponent } from 'react';
import { logger } from './logger';
import { isChunkLoadError } from './chunkLoadErrorUtils';

/**
 * Error component displayed when chunk loading fails
 */
const ChunkLoadErrorComponent: React.FC<{ error?: Error; retry?: () => void }> = ({ error, retry }) => {
  return (
    <div style={{ 
      padding: '20px', 
      textAlign: 'center',
      fontFamily: 'Arial, sans-serif',
      maxWidth: '600px',
      margin: '50px auto',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      backgroundColor: '#fafafa'
    }}>
      <h2 style={{ color: '#d32f2f', marginBottom: '16px' }}>Unable to Load Component</h2>
      <p style={{ color: '#666', marginBottom: '16px' }}>
        The component failed to load. This may be due to a network issue or an outdated cache.
      </p>
      {error && (
        <details style={{ marginBottom: '16px', textAlign: 'left' }}>
          <summary style={{ cursor: 'pointer', color: '#1976d2', marginBottom: '8px' }}>
            Error Details
          </summary>
          <pre style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '12px', 
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '12px'
          }}>
            {error.message}
          </pre>
        </details>
      )}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Reload Page
        </button>
        {retry && (
          <button
            onClick={retry}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Creates a safe lazy-loaded component with error handling
 * 
 * @param importFn - Function that returns a promise for the component module
 * @param componentName - Name of the component (for logging purposes)
 * @returns Lazy-loaded component with error handling
 * 
 * @example
 * ```typescript
 * const SafeDashboardPage = safeLazy(
 *   () => import('./pages/DashboardPage'),
 *   'DashboardPage'
 * );
 * ```
 */
export function safeLazy<P = {}>(
  importFn: () => Promise<{ default: ComponentType<P> }>,
  componentName?: string
): LazyExoticComponent<ComponentType<P>> {
  const name = componentName || 'Component';
  const maxAttempts = 3;
  const baseDelayMs = 250;
  
  return lazy(async () => {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const module = await importFn();

        if (!module.default) {
          throw new Error(`Module ${name} does not have a default export`);
        }

        if (attempt > 1) {
          logger.debug(`[SafeLazy] Recovered chunk for ${name} on attempt ${attempt}`);
        }
        else {
          logger.debug(`[SafeLazy] Successfully loaded chunk for ${name}`);
        }
        return module;
      }
      catch (error) {
        const castError = error as Error;
        lastError = castError;
        const retryableChunkError = isChunkLoadError(castError);
        const shouldRetry = retryableChunkError && attempt < maxAttempts;

        logger.error(`[SafeLazy] Failed to load chunk for ${name}`, castError, {
          componentName: name,
          errorType: castError?.name || 'Unknown',
          errorMessage: castError?.message || String(error),
          attempt,
          maxAttempts,
          retrying: shouldRetry,
        });

        if (!shouldRetry) {
          break;
        }

        const waitMs = baseDelayMs * attempt;
        await new Promise((resolve) => setTimeout(resolve, waitMs));
      }
    }

    // Re-throw so ErrorBoundary can handle fallback and guarded reload.
    throw lastError ?? new Error(`Failed to load chunk for ${name}`);
  });
}

/**
 * Creates a lazy-loaded component that shows a fallback UI on error
 * Note: This requires the component to be wrapped in an ErrorBoundary
 * 
 * @param importFn - Function that returns a promise for the component module
 * @param componentName - Name of the component (for logging purposes)
 * @returns Lazy-loaded component
 */
export function safeLazyWithFallback<P = {}>(
  importFn: () => Promise<{ default: ComponentType<P> }>,
  componentName?: string
): LazyExoticComponent<ComponentType<P>> {
  return safeLazy(importFn, componentName);
}

export { ChunkLoadErrorComponent };
